import { CircleAlert } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useI18n } from '../i18n/I18nProvider';
import styles from './Feedback.module.css';

export interface SummaryItem {
  /** Unique per item (issue path + key). */
  id: string;
  /** Human label of the field, or null for problems that belong to the whole form. */
  label: string | null;
  message: string;
  /** data-field name to move focus to, when the field is on this page. */
  fieldName?: string | undefined;
}

const FOCUSABLE = 'input, textarea, select, button, [tabindex]:not([tabindex="-1"])';

/** Moves keyboard focus to a field rendered with data-field={name}. */
export function focusField(name: string): boolean {
  const quoted = name.replace(/["\\]/g, (c) => `\\${c}`);
  const container = document.querySelector<HTMLElement>(`[data-field="${quoted}"]`);
  if (!container) return false;
  const target = container.matches(FOCUSABLE)
    ? container
    : container.querySelector<HTMLElement>(FOCUSABLE);
  if (!target) return false;
  // Not every environment implements smooth scrolling (e.g. test DOMs); focus still scrolls.
  if (typeof target.scrollIntoView === 'function') {
    target.scrollIntoView({ block: 'center', behavior: 'smooth' });
    target.focus({ preventScroll: true });
  } else {
    target.focus();
  }
  return true;
}

/**
 * Error summary shown above a form when the server rejects it: every issue, in the user's language,
 * with a link that moves focus to the field. It takes focus when it appears (screen readers read it).
 */
export function FormErrors({ items, title }: { items: SummaryItem[]; title?: string }) {
  const { t } = useI18n();
  const ref = useRef<HTMLDivElement>(null);
  const signature = items.map((item) => item.id).join('|');

  useEffect(() => {
    if (signature) ref.current?.focus();
  }, [signature]);

  if (items.length === 0) return null;
  return (
    <div
      ref={ref}
      className={styles.summary}
      tabIndex={-1}
      role="alert"
      aria-labelledby="form-errors-title"
      data-testid="form-errors"
    >
      <h2 id="form-errors-title" className={styles.summaryTitle}>
        <CircleAlert size={18} aria-hidden="true" />
        {title ?? t.errors.summaryTitle(items.length)}
      </h2>
      <ul className={styles.summaryList}>
        {items.map((item) => (
          <li key={item.id}>
            {item.fieldName ? (
              <button
                type="button"
                className={styles.summaryLink}
                onClick={() => {
                  if (item.fieldName) focusField(item.fieldName);
                }}
              >
                {item.label ? <span className={styles.summaryField}>{item.label}: </span> : null}
                {item.message}
              </button>
            ) : (
              <span>
                {item.label ? <span className={styles.summaryField}>{item.label}: </span> : null}
                {item.message}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
